import Layers from '@mui/icons-material/Layers';
import { IconButton, Toolbar } from '@mui/material';
import { useBoolean } from '@qfa/react-components';
import clsx from 'clsx';
import { DocumentPath, DocumentationButton } from 'components/layouts';

import L from 'leaflet';
import React, { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import EntityDrawerButton from './EntitiesDrawerButton';
import LayersDrawer from './LayersDrawer';
import MapLegend from './MapLegend';
import StatusBarButton from './StatusBarButton';
import useLayersToolbarStyles from './styles';
import { LayersToolbarProps } from './types';
import LayerExtentPreferences from './LayerExtentPreferences';
import { ResetViewButton } from './ResetViewButton/ResetViewButton';
import { PanTool } from './PanTool';
import { DeckLassoTool } from './LassoTool/DeckLassoTool';
import DistanceTool from './DistanceTool';
import AreaTool from './AreaTool';
import ExternalLinkButton from 'components/layouts/PageHeader/ExternalLinkButton/ExternalLinkButton';

const LayersToolbar: React.FunctionComponent<LayersToolbarProps> = ({
  showEntityDrawerButton = false,
}) => {
  const { t } = useTranslation(['common']);
  const [{ toggle: toggleLayers, off }, isLayersOpen] = useBoolean();
  const classes = useLayersToolbarStyles();

  const toolbarWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (toolbarWrapperRef?.current) {
      L.DomEvent.disableClickPropagation(toolbarWrapperRef.current);
      L.DomEvent.disableScrollPropagation(toolbarWrapperRef.current);
    }
  });

  const hideLayers = () => {
    if (isLayersOpen) {
      off();
    }
  };

  return (
    <>
      <div className={classes.toolbarWrapper} ref={toolbarWrapperRef}>
        <Toolbar id='LayersToolbar' variant='dense' disableGutters className={classes.toolbar}>
          <IconButton
            className={clsx(classes.button, { [classes.buttonActive]: isLayersOpen })}
            onClick={toggleLayers}
            aria-label={t('common:mapLayers')}
          >
            <Layers />
          </IconButton>
          <PanTool />
          <DeckLassoTool />
          <DistanceTool />
          <AreaTool />
          <ResetViewButton />
          <span style={{ marginLeft: 'auto' }}></span>
          <StatusBarButton />
          <LayerExtentPreferences className={clsx(classes.button)} />
          {showEntityDrawerButton && <EntityDrawerButton />}
          <MapLegend />
          <DocumentationButton document={DocumentPath['MAP']} />
          <ExternalLinkButton documentPath={'MAP'} />
        </Toolbar>
        <LayersDrawer open={isLayersOpen} onClickAway={hideLayers} />
      </div>
    </>
  );
};

export default memo(LayersToolbar);
